/**
 * CountryGlobe - POC for country-colored globe visualization.
 *
 * Renders a globe with colored countries and black borders using texture baking.
 * Uses the same dual-position geometry pattern as MorphingHemisphere and TileMesh.
 */

import { useMemo, useRef, useEffect } from 'react'
import * as THREE from 'three'
import { GLOBE_CONFIG } from '@/domain/globe/constants'
import { filterProvincesByZoom } from '@/domain/globe/constants/provinceVisibility'
import { CountryTextureGenerator } from '@/domain/globe/services/TextureGenerator'
import {
  countryGlobeMorphVertexShader,
  countryGlobeFragmentShader,
} from '@/domain/globe/shaders'
import { createHemisphereGeometry } from '@/domain/globe/utils'
import type { CountryData, LakeData, ProvinceData } from '@/domain/globe/types/features'

interface CountryGlobeProps {
  /** Country data to render */
  countries: CountryData[]
  /** Lake data to render (overlaid on countries) */
  lakes?: LakeData[]
  /** Province data to render (borders at higher zoom) */
  provinces?: ProvinceData[]
  /** Current zoom level for province visibility filtering */
  zoom?: number
  /** Radius of the hemisphere */
  radius?: number
  /** Number of segments for geometry detail */
  segments?: number
  /** Curvature factor: 0 = flat plane, 1 = full hemisphere */
  curvature?: number
}

/**
 * CountryGlobe component - POC implementation.
 */
export function CountryGlobe({
  countries,
  lakes = [],
  provinces = [],
  zoom = 5,
  radius = GLOBE_CONFIG.SPHERE_RADIUS,
  segments = GLOBE_CONFIG.WIDTH_SEGMENTS,
  curvature = 1.0,
}: CountryGlobeProps) {
  const materialRef = useRef<THREE.ShaderMaterial>(null)

  // Track textures for proper cleanup
  const texturesRef = useRef<{
    country: THREE.CanvasTexture | null
    border: THREE.CanvasTexture | null
    provinceBorder: THREE.CanvasTexture | null
    landMask: THREE.CanvasTexture | null
    glow: THREE.CanvasTexture | null
  }>({ country: null, border: null, provinceBorder: null, landMask: null, glow: null })

  // Filter provinces by zoom level
  const visibleProvinces = useMemo(
    () => filterProvincesByZoom(provinces, zoom),
    [provinces, zoom]
  )

  // Generate textures from country and lake data (excluding provinces - those change with zoom)
  const { countryTexture, borderTexture, landMaskTexture, glowTexture } = useMemo(() => {
    // Dispose previous textures before creating new ones
    texturesRef.current.country?.dispose()
    texturesRef.current.border?.dispose()
    texturesRef.current.landMask?.dispose()
    texturesRef.current.glow?.dispose()

    const generator = new CountryTextureGenerator(4096, 2048) // Full resolution

    const newTextures = {
      countryTexture: generator.generateCountryTexture(countries, lakes),
      borderTexture: generator.generateBorderTexture(countries, lakes),
      landMaskTexture: generator.generateLandMaskTexture(countries, lakes),
      glowTexture: generator.generateGlowTexture(countries),
    }

    // Store references for future cleanup
    texturesRef.current.country = newTextures.countryTexture
    texturesRef.current.border = newTextures.borderTexture
    texturesRef.current.landMask = newTextures.landMaskTexture
    texturesRef.current.glow = newTextures.glowTexture

    return newTextures
  }, [countries, lakes])

  // Generate province border texture separately (changes with zoom)
  const provinceBorderTexture = useMemo(() => {
    // Dispose previous province texture
    texturesRef.current.provinceBorder?.dispose()

    const generator = new CountryTextureGenerator(4096, 2048)
    const newTexture = generator.generateProvinceBorderTexture(visibleProvinces)

    // Store reference for cleanup
    texturesRef.current.provinceBorder = newTexture

    return newTexture
  }, [visibleProvinces])

  // Cleanup textures on unmount
  useEffect(() => {
    return () => {
      texturesRef.current.country?.dispose()
      texturesRef.current.border?.dispose()
      texturesRef.current.provinceBorder?.dispose()
      texturesRef.current.landMask?.dispose()
      texturesRef.current.glow?.dispose()
    }
  }, [])

  // Create geometry once
  const geometry = useMemo(
    () => createHemisphereGeometry(radius, segments, segments / 2, { flipV: false }),
    [radius, segments]
  )

  // Shader uniforms
  const uniforms = useMemo(
    () => ({
      curvature: { value: curvature },
      countryTexture: { value: countryTexture },
      borderTexture: { value: borderTexture },
      provinceBorderTexture: { value: provinceBorderTexture },
      landMaskTexture: { value: landMaskTexture },
      glowTexture: { value: glowTexture },
    }),
    [curvature, countryTexture, borderTexture, provinceBorderTexture, landMaskTexture, glowTexture]
  )

  // Update curvature uniform when it changes (matches TileMesh pattern)
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.curvature.value = curvature
    }
  }, [curvature])

  // Use key to force remount when base data changes
  const meshKey = `country-globe-${countries.length}-${lakes.length}-${provinces.length}`

  return (
    <mesh key={meshKey} name="country-globe" geometry={geometry} renderOrder={1}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={countryGlobeMorphVertexShader}
        fragmentShader={countryGlobeFragmentShader}
        uniforms={uniforms}
        side={THREE.DoubleSide}
        transparent={false}
      />
    </mesh>
  )
}
